import { useCallback, useEffect, useMemo, useState } from 'react';
import SidePanel from 'src/components/design-system/SidePanel';
import useAuth from 'src/hooks/useAuth';
import useConfirm from 'src/hooks/useConfirm';
import usePeriodView from 'src/hooks/usePeriodView';
import useGetManagementCompanyApi from 'src/services/useGetManagementCompanyApi';
import useImportBudgetApi from 'src/services/useImportBudgetApi';
import useImportUploadS3Url from 'src/services/useImportFileUploadS3Url';
import useImportS3Url from 'src/services/useImportS3Url';
import Export, { fiscalYearType } from './Export';
import Import from './Import';
import DarkBackgroundLoadingPanel from 'src/components/DarkBackgroundLoadingPanel';

interface props {
  isImportable: boolean;
}

const ImportSidePanel = ({ isImportable }: props) => {
  const { isSuperAdmin } = useAuth();
  const { isImportExportPanelOpen, closeImportExportPanelOpen } = usePeriodView();
  const [selectedTab, setSelectedTab] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [fiscalYear, setFiscalYear] = useState<fiscalYearType | null>(null);
  const [amountType, setAmountType] = useState<'budget' | 'forecast'>('forecast');
  const isImportExportDirty = useMemo(
    () => file !== null || fiscalYear !== null || amountType !== 'forecast',
    [amountType, file, fiscalYear]
  );
  const { confirm } = useConfirm();
  const resetToInitialValue = useCallback(() => {
    setFile(null);
    setFiscalYear(null);
    setAmountType('forecast');
  }, []);

  const { execute, s3Loading } = useImportS3Url();
  const { urlExecute, fileUploadLoading } = useImportUploadS3Url();
  const { importExecute, importLoading } = useImportBudgetApi();

  const handleSelect = useCallback(
    async (selectedTab: number) => {
      if (isImportExportDirty) {
        const res = await confirm({
          title: 'Unsaved Changes',
          content:
            'You have unsaved changes. Please go back (Cancel) to first save them. Otherwise, you can proceed and your changes will be lost.',
          cancelButtonText: 'Cancel',
          confirmButtonText: 'Discard and Proceed',
          width: '725px',
        });
        if (!res) {
          return;
        }
      }
      setSelectedTab(selectedTab);
      resetToInitialValue();
    },
    [confirm, isImportExportDirty, resetToInitialValue]
  );

  const handleClose = useCallback(async () => {
    if (s3Loading || fileUploadLoading || importLoading) {
      return;
    } else {
      let response;
      if (isImportExportDirty) {
        response = await confirm({
          title: 'Unsaved Changes',
          content:
            'You have unsaved changes. Please go back (Cancel) to first save them. Otherwise, you can proceed and your changes will be lost.',
          cancelButtonText: 'Cancel',
          confirmButtonText: 'Discard and Proceed',
          width: '725px',
        });
        if (!response) {
          return;
        }
      }
      closeImportExportPanelOpen();
      resetToInitialValue();
      setSelectedTab(0);
    }
  }, [
    closeImportExportPanelOpen,
    confirm,
    fileUploadLoading,
    importLoading,
    isImportExportDirty,
    resetToInitialValue,
    s3Loading,
  ]);

  const { managementCompanies, isManagementCompanyLoading, getManagementCompany } =
    useGetManagementCompanyApi();

  useEffect(() => {
    if (isSuperAdmin && isImportExportPanelOpen) {
      getManagementCompany();
    }
  }, [getManagementCompany, isImportExportPanelOpen, isSuperAdmin]);

  const [fileDownloadLoading, setFileDownloadLoading] = useState(false);

  return (
    <>
      {fileDownloadLoading && (
        <div className="export-loader-bg">
          <DarkBackgroundLoadingPanel id="checkbookConfigurationTable" />
        </div>
      )}
      <SidePanel
        title={`${isImportable ? 'Import/Export Checkbook' : 'Export Checkbook'}`}
        isOpen={isImportExportPanelOpen}
        onClose={handleClose}
        width="450px"
        className="bg-[#EEEEEE]"
      >
        {isImportable && (
          <div className="department-panel-tabs flex items-center gap-4 mt-4">
            <div className="flex w-full">
              {['Export', 'Import'].map((label, index) => (
                <button
                  key={index}
                  onClick={() => handleSelect(index)}
                  className={`tabs px-4 text-gray-500 hover:text-gray-700 focus:outline-none w-full ${
                    selectedTab === index
                      ? 'border-t-3 border-blue-500 border-opacity-70 active-tab'
                      : 'border-t-3 border-gray-500 bg-gray-300 border-opacity-70 inactive-tab'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          {selectedTab === 0 ? (
            <Export
              isManagementCompanyLoading={isManagementCompanyLoading}
              managementCompanies={managementCompanies}
              fileDownloadLoading={fileDownloadLoading}
              setFileDownloadLoading={setFileDownloadLoading}
            />
          ) : (
            <Import
              handleClose={handleClose}
              amountType={amountType}
              file={file}
              fiscalYear={fiscalYear}
              setAmountType={setAmountType}
              setFile={setFile}
              setFiscalYear={setFiscalYear}
              execute={execute}
              urlExecute={urlExecute}
              importExecute={importExecute}
              importLoading={importLoading}
              s3Loading={s3Loading}
              fileUploadLoading={fileUploadLoading}
            />
          )}
        </div>
      </SidePanel>
    </>
  );
};
export default ImportSidePanel;
